import { useState } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { Camera } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import PhotoUploadModal from "./PhotoUploadModal";
import { useImageUpload } from '@/hooks/useImageUpload';

interface NewLostItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  useDropdown?: boolean;
  onSubmit?: (item: any) => void;
}

const LOCATION_OPTIONS = [
  { value: "Pool", label: "Pool" },
  { value: "Clubhouse", label: "Clubhouse" },
  { value: "Street", label: "Street" },
  { value: "Others", label: "Others" }
];

const NewLostItemModal = ({ 
  open, 
  onOpenChange,
  useDropdown,
  onSubmit
}: NewLostItemModalProps) => {
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [unitNumber, setUnitNumber] = useState("");
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [foundBy, setFoundBy] = useState("");
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [tempItemId] = useState(() => `temp_${Date.now()}`);
  const { uploadImage, isUploading } = useImageUpload();
  const [photoPublicUrl, setPhotoPublicUrl] = useState<string | null>(null);
  
  const handleSubmit = async () => {
    console.log('Register clicked');
    if (!description.trim()) {
      toast.error("Please enter a description");
      return;
    }
    if (!location) {
      toast.error("Please select a location");
      return;
    }
    let finalPhotoUrl = photoPublicUrl;
    // Only upload if photoPreview is a base64 string
    if (photoPreview && photoPreview.startsWith('data:')) {
      finalPhotoUrl = await uploadImage(photoPreview, tempItemId);
      setPhotoPublicUrl(finalPhotoUrl || null);
    } else if (photoPreview && !photoPreview.startsWith('data:')) {
      finalPhotoUrl = photoPreview;
      setPhotoPublicUrl(finalPhotoUrl);
    }
    const newItem: any = {
      description,
      location,
      unitNumber,
      photo: finalPhotoUrl || null,
      found_by: foundBy
    };
    console.log('onSubmit is', onSubmit);
    if (onSubmit) {
      await onSubmit(newItem);
      console.log('onSubmit finished');
    }
    resetForm();
    onOpenChange(false);
  };
  
  const resetForm = () => {
    setDescription("");
    setLocation("");
    setUnitNumber("");
    setPhotoPreview(null);
    setFoundBy("");
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Register New Lost Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <textarea
                className="w-full min-h-[100px] px-3 py-2 text-sm border rounded-md"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the lost item..."
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium">Location</label>
              <Select value={location} onValueChange={setLocation}>
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {LOCATION_OPTIONS.map(loc => (
                    <SelectItem key={loc.value} value={loc.value}>
                      {loc.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label htmlFor="unitNumber" className="block text-sm font-medium">
                Unit Number
              </label>
              <Input
                id="unitNumber"
                type="text"
                value={unitNumber}
                onChange={(e) => setUnitNumber(e.target.value)}
                placeholder="123"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="foundBy" className="block text-sm font-medium">
                Who found the item?
              </label>
              <Input
                id="foundBy"
                type="text"
                value={foundBy}
                onChange={(e) => setFoundBy(e.target.value)}
                placeholder="Staff name"
              />
            </div>
            
            {/* Photo upload */}
            <div className="space-y-2">
              <label className="block text-sm font-medium">
                Take Photo or Upload (Optional)
              </label>
              <div className="flex items-center gap-2">
                {photoPreview ? (
                  <div className="relative w-full">
                    <img 
                      src={photoPreview} 
                      alt="Preview" 
                      className="w-full h-32 object-cover rounded-md" 
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="absolute bottom-2 right-2"
                      onClick={() => setPhotoModalOpen(true)}
                    >
                      Change Photo
                    </Button>
                  </div>
                ) : (
                  <Button 
                    type="button" 
                    variant="outline"
                    className="w-full h-32 flex flex-col items-center justify-center gap-2"
                    onClick={() => setPhotoModalOpen(true)}
                  >
                    <Camera size={24} />
                    <span>Add Photo</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                resetForm();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              Register
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PhotoUploadModal
        open={photoModalOpen}
        imageUrl={photoPreview}
        onClose={() => setPhotoModalOpen(false)}
        onSave={async (newPhoto) => {
          setPhotoPreview(newPhoto);
          // Only upload if newPhoto is a base64 string
          if (newPhoto && newPhoto.startsWith('data:')) {
            const publicUrl = await uploadImage(newPhoto, tempItemId);
            setPhotoPublicUrl(publicUrl || null);
          } else if (newPhoto) {
            setPhotoPublicUrl(newPhoto);
          }
          setPhotoModalOpen(false);
        }}
        itemId={tempItemId}
        enableOcr={false}
      />
    </>
  );
};

export default NewLostItemModal;
